import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, useReducedMotion } from 'framer-motion';
import { initLenis, destroyLenis, getLenis } from '@/lib/scroll';
import LoadingScreen from '@/components/LoadingScreen';
import CustomCursor from '@/components/CustomCursor';
import ScrollProgress from '@/components/ScrollProgress';
import ParticleBackground from '@/components/ParticleBackground';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import HomePage from '@/pages/HomePage';
import UniversePage from '@/pages/UniversePage';
import ToonsPage from '@/pages/ToonsPage';
import ToonProfilePage from '@/pages/ToonProfilePage';
import StoryPage from '@/pages/StoryPage';
import EcosystemPage from '@/pages/EcosystemPage';
import MarketPage from '@/pages/MarketPage';
import CommunityPage from '@/pages/CommunityPage';
import NewsPage from '@/pages/NewsPage';
import AdminPage from '@/pages/AdminPage';
import NotFoundPage from '@/pages/NotFoundPage';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    const lenis = getLenis();
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  const [loaded, setLoaded] = useState(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    initLenis(reduce);
    let raf;
    const loop = (t) => {
      const lenis = initLenis(reduce);
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      destroyLenis();
    };
  }, [reduce]);

  useEffect(() => {
    const lenis = initLenis(reduce);
    if (loaded) lenis.start();
    else lenis.stop();
  }, [loaded, reduce]);

  const handleLoaded = useCallback(() => setLoaded(true), []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <div
        className="noise relative min-h-screen overflow-x-clip bg-navy font-body text-snow"
        data-testid="app-root"
      >
        <CustomCursor />
        <ScrollProgress />
        <AnimatePresence>{!loaded && <LoadingScreen onComplete={handleLoaded} />}</AnimatePresence>
        <ParticleBackground />
        <Navbar loaded={loaded} />

        <main className="relative z-10">
          <Routes>
            <Route path="/" element={<HomePage started={loaded} />} />
            <Route path="/universe" element={<UniversePage />} />
            <Route path="/toons" element={<ToonsPage />} />
            <Route path="/toons/:id" element={<ToonProfilePage />} />
            <Route path="/story" element={<StoryPage />} />
            <Route path="/ecosystem" element={<EcosystemPage />} />
            <Route path="/market" element={<MarketPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </BrowserRouter>
  );
}
